import React, { useEffect, useState, useContext, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  LoadingContext, M_StateActions, MHeader, MButton, NavigateContext
} from 'src/M_Components';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from 'src/components/Forms/DynamicForm';
import '../pages.css';

export default function TransportTypeConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const selectedTab = useSelector((state) => state.StateKeyValue.find(o => o.key === 'selectedTab')?.value); // observe only selectedTab
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  const globalOperation = M_State.find(obj => obj.key === 'operation')?.value;
  const globalLine = M_State.find(obj => obj.key === 'TransportTypeLine')?.value;

  const [id, setId] = useState(selectedTab);
  const [operation, setOperation] = useState(globalOperation ?? 'View'); // default to View for stability
  const [passLine, setPassLine] = useState(globalLine ?? {});
  const [transportTypeInfo, setTransportTypeInfo] = useState({});
  const [isFirst, setIsFirst] = useState(true);

  const isViewMode = (operation || '').toLowerCase() === 'view';

  // Stable base url
  const baseUrl = ctx.DEFAULTURL;

  // Buffers memoized to avoid re-instantiation and re-renders
  const bufferService = useMemo(() => new BufferService('TransportTypes', baseUrl), [baseUrl]);
  const bufferServiceCreate = useMemo(() => new BufferService('TransportTypes/create', baseUrl), [baseUrl]);
  const bufferServiceUpdate = useMemo(() => new BufferService('TransportTypes/update', baseUrl), [baseUrl]);

  /* formRef to control DynamicForm submit programmatically */
  const formRef = useRef(null);

  /* guard refs */
  const flushingRef = useRef(false);
  const lastSnapshotRef = useRef(''); // serialized last snapshot to avoid re-dispatching same payload

  /* schema describing the fields for DynamicForm */
  //  remove static mock and use a stateful schema that is fetched from backend Options
  const [schema, setSchema] = useState([]); //  holds Options from backend

  //============================================================================================================
  // Session keys for restoring tab state
  //============================================================================================================
  const sessionKeys =
    [
      'passLine',
      'operation',
      'transportTypeInfo',
      'isFirst',
      'df.schema', //  persist backend Options in session to preserve across tab switches
    ];

  // small helper to compare snapshots cheaply
  const serializeSnapshot = (arr) => {
    try { return JSON.stringify(arr); } catch { return ''; }
  };

  //============================================================================================================
  // Effect to rename tab on  id change, transportTypeInfo.Name change, or operation change
  //============================================================================================================
  useEffect(() => {
    if (!id) return;

    const name = transportTypeInfo?.Name?.trim();
    const op = (operation || '').toLowerCase();

    let label = '';
    if (op === 'create') {
      label = t('LabelNewTransportType');
    } else if (op === 'edit') {
      label = name ? `${t('LabelEditTransportType')} - ${name}` : t('LabelEditTransportType');
    } else {
      label = name ? `${t('LabelViewTransportType')} - ${name}` : t('LabelViewTransportType');
    }

    renameTab(String(id), label);
  }, [id, transportTypeInfo?.Name, operation]);

  //============================================================================================================
  // Mount: restore per-tab snapshot befrore any fetch; then consume one-shot globals
  //============================================================================================================
  useEffect(() => {
    // Restore snapshot for this tab (prevents losing Create form values on remount)
    const sessionForThisTab = M_State.find(o => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find(x => x.key === k)?.value; // helper function to pick values from session

    // Pick session values
    const snapPL = pick('passLine');
    const snapOP = pick('operation');
    const snapTI = pick('transportTypeInfo');
    const snapIF = pick('isFirst');
    const snapSchema = pick('df.schema'); //  restore saved Options

    // Restore session values
    if (snapTI) setTransportTypeInfo(snapTI);
    if (snapSchema) setSchema(Array.isArray(snapSchema) ? snapSchema : []); //  restore schema from session
    if (snapIF !== undefined) setIsFirst(snapIF); else if (isFirst) setIsFirst(false);

    // Prefer one-shot globals; fall back to snapshot
    const seedOperation = (globalOperation !== undefined ? globalOperation : snapOP);
    const seedLine = (globalLine !== undefined ? globalLine : snapPL);

    if (seedOperation !== undefined) setOperation(seedOperation);
    if (seedLine) setPassLine(seedLine);

    // Clear one-shot globals to preserve tab independence
    if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
    if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'TransportTypeLine' }));

    // Initial fetch: fetch Options always; details only if NOT Create and with valid Id
    //  ensure Options come from backend to keep in sync with server metadata
    bootstrap(seedLine, seedOperation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //============================================================================================================
  // Persist snapshot on unmount (safety net to keep Create drafts)
  //============================================================================================================
  useEffect(() => {
    return () => {
      // ensure we capture the latest typed values even without blur
      const current = formRef.current?.getValues?.() || {};
      const infoToPersist =
        (operation || '').toLowerCase() === 'view'
          ? (transportTypeInfo || {})
          : { ...(transportTypeInfo || {}), ...current };

      const vals = [passLine, operation, infoToPersist, isFirst, schema];
      const nextArray = sessionKeys.map((key, idx) => ({ key: `${key}`, value: vals[idx] }));
      const snap = serializeSnapshot(nextArray);
      if (snap !== lastSnapshotRef.current) {
        lastSnapshotRef.current = snap;
        dispatch(M_StateActions.addM_State({ key: id, value: nextArray }));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, passLine, operation, transportTypeInfo, isFirst, schema, dispatch]);

  //============================================================================================================
  // React only to selectedTab changes (not every M_State change)
  // Flush draft before switching and then hydrate the new tab
  //============================================================================================================
  useEffect(() => {
    if (!selectedTab || selectedTab === id) return;

    if (flushingRef.current) return;
    flushingRef.current = true;

    try {
      const currentFormValues = formRef.current?.getValues?.() || {};
      const infoToPersist =
        (operation || '').toLowerCase() === 'view'
          ? (transportTypeInfo || {})
          : { ...(transportTypeInfo || {}), ...currentFormValues };

      const vals = [passLine, operation, infoToPersist, isFirst, schema];
      const nextArray = sessionKeys.map((key, idx) => ({ key: `${key}`, value: vals[idx] }));
      const snap = serializeSnapshot(nextArray);
      if (snap !== lastSnapshotRef.current) {
        lastSnapshotRef.current = snap;
        dispatch(M_StateActions.addM_State({ key: id, value: nextArray }));
      }

      setId(selectedTab);
      getSessionStorageData(selectedTab);
    } finally {
      flushingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]); // depends only on selectedTab

  //============================================================================================================
  // Persist snapshot to M_State (replicates other pages)
  // - Important: in create/edit we merge current RHF values so drafts persist without blur
  //============================================================================================================
  useEffect(() => {
    if (!id) return;

    // read live RHF values to capture drafts while typing
    const currentFormValues = formRef.current?.getValues?.() || {};
    const infoToPersist =
      (operation || '').toLowerCase() === 'view'
        ? transportTypeInfo
        : { ...transportTypeInfo, ...currentFormValues };

    const vals = [passLine, operation, infoToPersist, isFirst, schema];
    const nextArray = sessionKeys.map((key, idx) => ({ key: `${key}`, value: vals[idx] }));
    const snap = serializeSnapshot(nextArray);
    if (snap !== lastSnapshotRef.current) {
      lastSnapshotRef.current = snap;
      dispatch(M_StateActions.addM_State({ key: id, value: nextArray }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, passLine, operation, transportTypeInfo, isFirst, schema]); // keep in sync with other pages

  //============================================================================================================
  //  Refetch Options on language change to update NormalizedName and enum labels
  //============================================================================================================
  useEffect(() => {
    if (!operation || isFirst) return;
    fetchOptions(); //  re-pull Options for translated labels

    const name = transportTypeInfo?.Name?.trim();
    const op = (operation || '').toLowerCase();

    let label = '';
    if (op === 'create') {
      label = t('LabelNewTransportType');
    } else if (op === 'edit') {
      label = name ? `${t('LabelEditTransportType')} - ${name}` : t('LabelEditTransportType');
    } else {
      label = name ? `${t('LabelViewTransportType')} - ${name}` : t('LabelViewTransportType');
    }

    renameTab(String(id), label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  //============================================================================================================
  // Function to retrieve data from session storage based on the session ID
  //============================================================================================================
  const getSessionStorageData = (sessionID) => {
    ctx.setIsLoad(true);

    const M_StateData = M_State.find(obj => obj.key === sessionID)?.value; // Find the session data in M_State
    if (M_StateData) { // If session data exists, pick values from it
      const pick = (k) => M_StateData.find(obj => obj.key === k)?.value;
      const tempPL = pick('passLine');
      const tempO = pick('operation');
      const tempTI = pick('transportTypeInfo');
      const tempIF = pick('isFirst');
      const tempSchema = pick('df.schema'); //  pick schema from session

      // Set session values
      if (tempPL || tempO || tempTI || tempSchema) { //  include schema presence in guard
        setPassLine(tempPL || {});
        setOperation(tempO ?? 'View');
        setTransportTypeInfo(tempTI || {});
        if (tempSchema) setSchema(Array.isArray(tempSchema) ? tempSchema : []); //  
        setIsFirst(tempIF ?? false);
        ctx.setIsLoad(false);
        return;
      }
    }
    // If no session data found, refresh with the current passLine
    bootstrap(passLine, operation); //  call bootstrap to load Options and details accordingly
  };

  //============================================================================================================
  //  bootstrap pipeline to ensure Options are loaded before details
  //============================================================================================================
  const bootstrap = (line, op = operation) => {
    ctx.setIsLoad(true);
    Promise.resolve()
      .then(() => fetchOptions()) // always load Options first
      .then(() => {
        // Guard: only fetch entity when NOT creating AND id is valid
        if ((op || '').toLowerCase() === 'create' || !line?.Id) return;
        return fetchDetails(line?.Id);
      })
      .finally(() => ctx.setIsLoad(false));
  };

  //============================================================================================================
  //  fetch Options from backend instead of using a static mock
  //============================================================================================================
  const fetchOptions = async () => {
    try {
      const res = await bufferService.options();
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }
      const opts = Array.isArray(res) ? res : [];
      setSchema(opts);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    }
  };

  //============================================================================================================
  // Function to refresh the transport type information
  //============================================================================================================
  const getRefresh = (line, op = operation) => {
    // Guard: only fetch entity when NOT creating AND id is valid
    if ((op || '').toLowerCase() === 'create' || !line?.Id) {
      //  still refresh Options to stay in sync with backend schema
      fetchOptions();
      return;
    }
    ctx.setIsLoad(true);

    Promise.all([
      fetchOptions(), //  refresh Options together with details
      bufferService.getId(line?.Id)
        .then(response => {
          if (isFailurePayload(response)) {
            toastMessages('error', '', apiMessage(response, t('ErrorLoadingData')));
            return;
          }
          setTransportTypeInfo(response);
        })
        .catch(err => toastMessages('error', '', apiMessage(err, t('ErrorLoadingData'))))
    ])
      .finally(() => ctx.setIsLoad(false));
  };

  //============================================================================================================
  //  fetchDetails extracted for reuse in bootstrap
  //============================================================================================================
  const fetchDetails = async (idToGet) => {
    try {
      const response = await bufferService.getId(idToGet);
      if (isFailurePayload(response)) {
        toastMessages('error', '', apiMessage(response, t('ErrorLoadingData')));
        return;
      }
      setTransportTypeInfo(response);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    }
  };

  //============================================================================================================
  // Function to handle saving and updating the transport type information
  //============================================================================================================
  /*  delegate to DynamicForm submit to avoid duplicate validation paths */
  const onSave = () => {
    formRef.current?.submit?.();
  };

  /* onSubmit handler for DynamicForm that performs POST or PUT */
  const onSubmitDynamic = useCallback(async (values) => {
    ctx.setIsLoad(true);

    try {
      // Build the payload (Inactive only on edit)
      const payload = {
        name: values?.Name,
        ...(((operation || '').toLowerCase() !== 'create') && {
          inactive: !!values?.Inactive
        })
      };

      // Mandatory field: Name
      if (!payload.name) {
        toastMessages('error', '', t('MandatoryFields'));
        return;
      }

      const isCreate = (operation || '').toLowerCase() === 'create';
      const res = isCreate
        ? await bufferServiceCreate.post(payload)
        : await bufferServiceUpdate.update(passLine?.Id, payload);

      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
        return;
      }

      toastMessages('success', '', isCreate ? t('TransportTypeCreated') : t('TransportTypeUpdated'));
      handleTabClose(id);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
    } finally {
      ctx.setIsLoad(false);
    }
  }, [operation, passLine?.Id, id, t, toastMessages, bufferServiceCreate, bufferServiceUpdate, ctx]);

  /* keep local snapshot in sync when DF fields change, without emitting on every keystroke */
  const handleFormValuesChange = useCallback((patch) => {
    if (!patch) return;
    setTransportTypeInfo(prev => ({ ...prev, ...patch }));
  }, []);

  return (
    <>
      <MHeader element={
        <>
          <MButton // Button to refresh the table
            tooltip={t('RefreshTable')}
            icon={<i className='pi pi-refresh iconTable'></i>}
            onClick={() => getRefresh(passLine, operation)}
            label={t('RefreshTable')}
          />
          {!isViewMode && <MButton // Button to save the transport type information
            tooltip={t('Save')}
            icon={<i className='pi pi-save iconTable'></i>}
            onClick={onSave}
            label={t('Save')}
          />}
        </>
      } />

      <div
        style={{
          padding: '20px',
          background: 'var(--color-white)'
        }}>
        <div className='firstDivContainer'>
          <DynamicForm
            /* stable key per tab and entity to avoid stale form state when switching */
            key={`df-transporttype-${id}-${operation}-${passLine?.Id ?? 'new'}`}
            title={t('TransportType Information')}
            formRef={formRef}
            mode={(operation || 'view').toLowerCase()}
            schema={schema} //  schema now comes from backend Options
            data={transportTypeInfo}
            onValuesChange={handleFormValuesChange}
            onSubmit={onSubmitDynamic}
            containerClassName="alignInputsUserForm"
          />
        </div>
      </div>
    </>
  );
}

TransportTypeConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func
};
